/**
 * Post-login redirect sanitiser.
 *
 * `next` arrives from the query string (e.g. /auth/callback?next=...), so an
 * attacker controls it. Redirecting to `${origin}${next}` unchecked is an open
 * redirect: `next=.evil.com` becomes https://pebl-data.cloud.evil.com and
 * `next=@evil.com` becomes https://pebl-data.cloud@evil.com, both off-site.
 *
 * safeNextPath returns `raw` only when it is a same-origin relative path, and
 * the fallback otherwise. It accepts a value only if it:
 * - is a string of 1 to 2048 characters starting with a single "/";
 * - does not start with "//" or "/\" (protocol-relative, which browsers treat
 *   as another host);
 * - contains no backslash and no control character, raw or percent-decoded
 *   (so "/%2F%2Fevil.com" and "/%5Cevil.com" are rejected as well), and has
 *   no malformed percent-encoding;
 * - resolves against a dummy origin to that same origin.
 */

export const DEFAULT_NEXT_PATH = '/map-drawing';

export const MAX_NEXT_PATH_LENGTH = 2048;

// C0 controls, DEL and C1 controls. Browsers strip tabs and newlines from URLs,
// which can turn an innocent-looking value into a protocol-relative one.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

const PROBE_ORIGIN = 'http://safe-next-path.invalid';

function isUnsafeForm(value: string): boolean {
  return (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.startsWith('/\\') ||
    value.includes('\\') ||
    CONTROL_CHARS.test(value)
  );
}

export function safeNextPath(
  raw: string | null | undefined,
  fallback: string = DEFAULT_NEXT_PATH
): string {
  if (typeof raw !== 'string') return fallback;
  if (raw.length === 0 || raw.length > MAX_NEXT_PATH_LENGTH) return fallback;
  if (isUnsafeForm(raw)) return fallback;

  // Something downstream may decode before using the value, so the decoded
  // forms must be safe too. Up to three rounds catches double encoding.
  let current = raw;
  for (let round = 0; round < 3; round++) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return fallback; // malformed percent-encoding
    }
    if (isUnsafeForm(decoded)) return fallback;
    if (decoded === current) break;
    current = decoded;
  }

  // Final word goes to the URL parser: it must stay on the same origin
  try {
    const parsed = new URL(raw, PROBE_ORIGIN);
    if (parsed.origin !== PROBE_ORIGIN) return fallback;
  } catch {
    return fallback;
  }

  return raw;
}

/**
 * Absolute same-origin URL for a redirect after sign-in. `next` is passed
 * through safeNextPath, and the result is checked once more against `origin`.
 */
export function buildSafeRedirectUrl(
  origin: string,
  next: string | null | undefined,
  fallback: string = DEFAULT_NEXT_PATH
): string {
  const url = new URL(safeNextPath(next, fallback), origin);
  if (url.origin !== new URL(origin).origin) {
    return new URL(fallback, origin).toString();
  }
  return url.toString();
}
